/**
 * Shared browser folder-walk for the upload dropzones.
 *
 * `webkitGetAsEntry` / the FileSystem entry API isn't in lib.dom typings, so we
 * model the slice we use. Extracted from DropzonePanel so FolderDropzone reuses
 * it verbatim (single-study and batch dropzones walk folders identically).
 */
export interface FsEntry {
  isFile: boolean;
  isDirectory: boolean;
  file?: (onok: (f: File) => void, onerr?: () => void) => void;
  createReader?: () => {
    readEntries: (onok: (entries: FsEntry[]) => void, onerr?: () => void) => void;
  };
}

/** Recursively collect every File under the given entries (folders included). */
export function collectFiles(entries: FsEntry[]): Promise<File[]> {
  const out: File[] = [];
  const walk = (entry: FsEntry): Promise<void> =>
    new Promise((resolve) => {
      if (entry.isFile && entry.file) {
        entry.file(
          (f) => {
            out.push(f);
            resolve();
          },
          () => resolve(),
        );
      } else if (entry.isDirectory && entry.createReader) {
        const reader = entry.createReader();
        // readEntries yields in batches; keep reading until a batch is empty.
        const readBatch = (): void =>
          reader.readEntries(
            (batch) => {
              if (batch.length === 0) {
                resolve();
                return;
              }
              Promise.all(batch.map(walk)).then(readBatch, () => resolve());
            },
            () => resolve(),
          );
        readBatch();
      } else {
        resolve();
      }
    });
  return Promise.all(entries.map(walk)).then(() => out);
}

/**
 * Synchronously pull the `webkitGetAsEntry` entries off a DataTransfer. MUST be
 * called inside the drop handler — `dataTransfer` and its entries are only valid
 * during the event, not after an await.
 */
export function readEntries(dt: DataTransfer): FsEntry[] {
  const entries: FsEntry[] = [];
  for (let i = 0; i < (dt.items?.length ?? 0); i++) {
    const item = dt.items[i] as DataTransferItem & { webkitGetAsEntry?: () => FsEntry | null };
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }
  return entries;
}
